import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

const CustomTurnstile = forwardRef(
  ({ siteKey, onSuccess, onError, onExpire, invisible }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [needsManualClick, setNeedsManualClick] = useState(false);
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);
    const isInitializedRef = useRef(false);

    useEffect(() => {
      const loadTurnstile = async () => {
        if (isInitializedRef.current) return;

        if (window.turnstile) {
          initializeTurnstile();
          return;
        }

        const existingScript = document.querySelector(
          'script[src*="challenges.cloudflare.com/turnstile"]'
        );

        if (existingScript) {
          if (!window.turnstile) {
            await new Promise((resolve) => {
              existingScript.addEventListener("load", resolve, { once: true });
            });
          }
          initializeTurnstile();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        script.async = true;
        script.defer = true;

        script.onload = () => {
          initializeTurnstile();
        };

        script.onerror = () => {
          console.error("Failed to load Cloudflare Turnstile script");
          onError?.();
        };

        document.body.appendChild(script);
      };

      loadTurnstile();

      return () => {
        // Cleanup
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (e) {
            console.error("Error removing turnstile widget:", e);
          }
        }
        widgetIdRef.current = null;
        isInitializedRef.current = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
          setIsLoading(false);
          setIsSuccess(false);
          setNeedsManualClick(false);
        }
      },
    }));

    const initializeTurnstile = () => {
      if (
        !window.turnstile ||
        !turnstileRef.current ||
        isInitializedRef.current
      ) {
        return;
      }

      try {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            console.log("✅ Turnstile verification successful");
            setIsLoading(false);
            setIsSuccess(true);
            setNeedsManualClick(false);
            onSuccess?.(token);
          },
          "error-callback": (error) => {
            console.error("❌ Turnstile error:", error);
            setIsLoading(false);
            setIsSuccess(false);
            onError?.("Verification failed. Please try again.");

            // Delayed reset to avoid immediate re-trigger
            setTimeout(() => {
              resetWidget();
            }, 1000);
          },
          "expired-callback": () => {
            console.log("⏰ Turnstile expired");
            setIsSuccess(false);
            setIsLoading(false);
            onExpire?.("Verification expired. Please try again.");

            // Delayed reset to avoid immediate re-trigger
            setTimeout(() => {
              resetWidget();
            }, 1000);
          },
          "timeout-callback": () => {
            console.log("⏱️ Turnstile timeout");
            setIsLoading(false);
            setIsSuccess(false);
            onError?.("Verification timed out. Please try again.");

            // Delayed reset to avoid immediate re-trigger
            setTimeout(() => {
              resetWidget();
            }, 1000);
          },
          theme: "dark",
          size: "compact",
          execution: "execute",
          "before-interactive-callback": () => {
            console.log("🔄 Turnstile widget becoming interactive");
          },
          "after-interactive-callback": () => {
            console.log("✨ Turnstile widget ready for interaction");
          },
        });

        isInitializedRef.current = true;
        // console.log("Turnstile initialized with ID:", widgetIdRef.current);

        // AUTO-EXECUTE sa fallback mogućnošću
        setTimeout(() => {
          if (widgetIdRef.current && window.turnstile && !isSuccess) {
            console.log("🚀 Auto-executing Turnstile verification...");
            setIsLoading(true);

            try {
              window.turnstile.execute(widgetIdRef.current);

              // Fallback: ako se zaglavi u loading 5s, omogući manual klik
              setTimeout(() => {
                if (isLoading && !isSuccess) {
                  console.log("⚠️ Auto-execute timeout, enabling manual click");
                  setIsLoading(false);
                  setNeedsManualClick(true);
                }
              }, 5000); // 5 sekundi timeout
            } catch (e) {
              console.error("Auto-execute failed:", e);
              setIsLoading(false);
            }
          }
        }, 500); // Delay za stabilnost

        // Dodatni timeout ako verification uopšte ne počne
        setTimeout(() => {
          if (!isLoading && !isSuccess && !needsManualClick) {
            console.log(
              "⚠️ Verification hasn't started after 3s, enabling manual click"
            );
            setNeedsManualClick(true);
          }
        }, 3000); // 3 sekunde timeout za početak verifikacije
      } catch (e) {
        console.error("Error rendering turnstile widget:", e);
        setIsLoading(false);
        onError?.();
      }
    };

    const resetWidget = () => {
      if (!window.turnstile || !widgetIdRef.current) return;

      try {
        window.turnstile.reset(widgetIdRef.current);
        setIsLoading(false);
        setIsSuccess(false);
        setNeedsManualClick(false);
        console.log("Widget reset successfully");
      } catch (e) {
        console.error("Error resetting widget:", e);
        setIsLoading(false);
        setIsSuccess(false);
        setNeedsManualClick(false);
      }
    };

    const handleClick = () => {
      // Ne dozvoli klik ako je već success
      if (isSuccess) {
        console.log("Already verified");
        return;
      }

      if (!window.turnstile || !widgetIdRef.current) {
        console.error("Turnstile not initialized");
        onError?.("Turnstile not ready");
        return;
      }

      // Ako je loading, prvo resetuj pa izvršij
      if (isLoading) {
        console.log("🔄 Resetting stuck verification...");
        resetWidget();

        // Čekaj malo pa probaj ponovo
        setTimeout(() => {
          if (window.turnstile && widgetIdRef.current) {
            console.log("🔁 Retrying verification...");
            setIsLoading(true);
            try {
              window.turnstile.execute(widgetIdRef.current);
            } catch (e) {
              console.error("Retry failed:", e);
              setIsLoading(false);
              onError?.("Verification failed");
            }
          }
        }, 500);
        return;
      }

      console.log("🎯 Manual verification triggered");
      setIsLoading(true);
      setNeedsManualClick(false);

      try {
        window.turnstile.execute(widgetIdRef.current);
      } catch (e) {
        console.error("Error executing turnstile:", e);
        setIsLoading(false);
        onError?.("Verification failed");
      }
    };

    return (
      <>
        <TurnstileContainer
          onClick={handleClick}
          style={{
            position: invisible ? "absolute" : "relative",
            zIndex: invisible ? -1000 : 1,
            opacity: invisible ? 0 : 1,
          }}
        >
          <CheckboxContainer $checked={isSuccess} $loading={isLoading}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <svg
                width="14"
                height="11"
                viewBox="0 0 14 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5.5L5 9.5L13 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </CheckboxContainer>
          <TurnstileText>
            <MainText>
              {isSuccess
                ? "Verified"
                : isLoading
                ? "Verifying..."
                : needsManualClick
                ? "Click to restart"
                : "I'm not a robot"}
            </MainText>
            <SubText>
              Protected by{" "}
              <CloudflareLink
                href="https://www.cloudflare.com/products/turnstile/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Cloudflare
              </CloudflareLink>
            </SubText>
          </TurnstileText>
        </TurnstileContainer>
        <HiddenTurnstile ref={turnstileRef} />
      </>
    );
  }
);

export default CustomTurnstile;

const TurnstileContainer = styled.div`
  width: 100%;
  height: 44px;
  background: #030303;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 12px;
  gap: 10px;
  flex-direction: row;
  cursor: pointer;

  /* Hover effect always enabled for fallback clicks */
  &:hover {
    background: #111;
  }
`;

const CheckboxContainer = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid ${(props) => (props.$checked ? "#4D9DFF" : "#3d3d45")};
  background: ${(props) =>
    props.$checked ? "#4D9DFF" : "rgba(255, 255, 255, 0.03)"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
  position: relative;

  ${(props) =>
    props.$loading &&
    `
    border-color: #2877D9;
  `}

  &:hover {
    border-color: ${(props) => (props.$checked ? "#22c55e" : "#2877D9")};
    transform: scale(1.05);
  }

  svg {
    opacity: ${(props) => (props.$checked ? 1 : 0)};
    transform: scale(${(props) => (props.$checked ? 1 : 0.5)});
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(40, 119, 217, 0.3);
  border-top-color: #2877d9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  position: absolute;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TurnstileText = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainText = styled.span`
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.21px;
`;

const SubText = styled.span`
  color: #fff;
  font-family: Figtree;
  opacity: 0.3;
  font-size: 12px;
  font-weight: 400;
`;

const CloudflareLink = styled.a`
  text-decoration: none;
  color: #fff;
  font-family: Figtree;
  font-size: 12px;
  font-weight: 400;

  &:hover {
    text-decoration: underline;
  }
`;

const HiddenTurnstile = styled.div`
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
  width: 300px;
  height: 65px;
`;
